import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { htmlToText } from "../../../emailBuilder/text";
import { getTemplate, listTemplates, type TemplateRow } from "../../../lib/marketingTemplates";
import { IconBtn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";

export interface TemplateSeed {
  subject: string;
  body: string;
}

/**
 * The letter templates, offered where a reply is written.
 *
 * They are the marketing module's templates rather than a second, mail-only
 * library: a company writes one set of standard answers, and a second store
 * would mean the good wording lives in whichever of the two the writer happened
 * to open. What is copied is the text part, not the design - the reply box is a
 * textarea, and a campaign's HTML shell around a two-line answer would arrive as
 * a newsletter.
 *
 * Merge tokens come through exactly as written. A one-to-one reply has no
 * audience row to fill them from, so leaving `{{name}}` visible is the honest
 * outcome: the operator sees it and replaces it. Quietly stripping them would
 * send a sentence with a hole in it.
 */
export function TemplateMenu({ onPick }: { onPick: (seed: TemplateSeed) => void }) {
  const t = useT();

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn(open && "is-on")}
          title={t("compose.template")}
          aria-label={t("compose.template")}
          aria-expanded={open}
        >
          <FileText className="size-4" />
        </IconBtn>
      )}
    >
      <TemplateList onPick={onPick} />
    </Pop>
  );
}

/** Mounted only while the menu is open, which is what makes the fetch happen on
 *  opening rather than on every render of the reading pane. */
function TemplateList({ onPick }: { onPick: (seed: TemplateSeed) => void }) {
  const t = useT();
  const close = useClosePops();
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listTemplates()
      .then((r) => alive && setRows(r.rows))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, []);

  async function pick(row: TemplateRow) {
    setBusy(row.id);
    try {
      const { template } = await getTemplate(row.id);
      // `bodyText` is authored alongside the HTML for real templates, but a
      // template built in the block editor can arrive with an empty one. The
      // same converter the campaign sender uses fills the gap, so the reply
      // reads the way the text part of that campaign would.
      onPick({
        subject: template.subject,
        body: template.bodyText.trim() || htmlToText(template.bodyHtml),
      });
      close();
    } catch {
      setBusy(null);
    }
  }

  if (rows === null) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className="crm-pal-empty">{t("compose.noTemplates")}</div>;
  }

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("compose.template")}</div>
        <div className="crm-menu-s">{t("compose.templateSub")}</div>
      </div>
      {rows.map((row) => (
        <button
          key={row.id}
          type="button"
          className="crm-mi"
          disabled={busy !== null}
          onClick={() => void pick(row)}
        >
          {busy === row.id ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
          <span className="min-w-0 flex-1">
            <span className="crm-trunc block">{row.name}</span>
            <span className="crm-mi-sub crm-trunc block">{row.subject || row.description || row.slug}</span>
          </span>
        </button>
      ))}
    </>
  );
}
